import { useCallback, useMemo } from 'react';
import { RotateCw } from 'lucide-react';
import { observer } from 'mobx-react-lite';
import {
  ODataGrid,
  type ODataGridConfig,
  type GridToolbarHelpers,
} from '@inuxe/prime-odata-grid';
import CommonButton from '../../../../components/common-components/button/CommonButton';
import { useStore } from '../../../../hooks/useStore';
import type { IUserManagementResult } from '../../../../models/user-management/UserManagementModel';
import { buildLegacyGridParams, buildODataMappingFromColumns } from '../../../../utils/odataGridUtils';
import toolbarStyles from '../../../../styles/ToolbarButtons.module.css';
import gridStyles from '../../../../styles/ODataGridPage.module.css';
import { useUserManagementColumns } from '../UserManagement.grid';

const GRID_ID = 'user-management-grid';

const UsersTab = () => {
  const { userManagementStore } = useStore();
  const columns = useUserManagementColumns();

  const oDataMapping = useMemo(
    () => buildODataMappingFromColumns(columns),
    [columns]
  );

  const gridConfig = useMemo<ODataGridConfig<IUserManagementResult>>(
    () => ({
      gridId: GRID_ID,
      rows: 20,
      oDataMapping,
      fetchRows: async params => {
        const result = await userManagementStore.listUsers(buildLegacyGridParams(params, { oDataMapping }));
        return { rows: result.rows, totalRecords: result.lastRow };
      },
    }),
    [userManagementStore, oDataMapping]
  );

  const onGridReady = useCallback(
    (helpers: GridToolbarHelpers<IUserManagementResult>) => {
      userManagementStore.setGridHelpers(helpers);
    },
    [userManagementStore]
  );

  return (
    <ODataGrid<IUserManagementResult>
      gridId={GRID_ID}
      config={gridConfig}
      columns={columns}
      dataKey="id"
      wrapperClassName={gridStyles.oDataGridPage}
      resizableColumns
      onGridReady={onGridReady}
      toolbarLeft={helpers => (
        <CommonButton
          type="refresh"
          onClick={() => helpers.refreshGrid()}
          leftIcon={<RotateCw size={18} strokeWidth={2} />}
          className={toolbarStyles.toolbarBtnRefresh}
        >
          Yenile
        </CommonButton>
      )}
    />
  );
};

export default observer(UsersTab);
